import apiClient from "./api";

/**
 * Forma vazia da resposta do dashboard.
 *
 * A tela renderiza a partir dela quando a API falha, então todo campo que o componente lê precisa
 * existir aqui — inclusive as listas. Uma lista ausente vira `.map is not a function` num painel
 * que deveria estar apenas mostrando zeros.
 *
 * `dias` acompanha o backend (DashboardService.DIAS_JANELA). É só o rótulo do cabeçalho: se um dia
 * o backend mudar a janela, o número certo chega junto com os dados e este aqui nunca aparece.
 */
const INDICADOR_VAZIO = { valor: 0, anterior: 0 };

const vazioPorPerfil = (perfil) => ({
    dias: 30,
    realizados: INDICADOR_VAZIO,
    // "investido" para o cliente, "receita" para o profissional — a mesma soma, nomes opostos.
    investido: INDICADOR_VAZIO,
    receita: INDICADOR_VAZIO,
    profissionais: INDICADOR_VAZIO,
    clientes: INDICADOR_VAZIO,
    agendadosFuturos: 0,
    avaliacao: { media: null, quantidade: 0 },
    pendencias: perfil === "profissional"
        ? { aguardandoResposta: 0 }
        : { avaliacoesPendentes: 0, aguardandoResposta: 0 },
    recorrentes: [],
    serie: []
});

/** Quantos compromissos futuros a faixa da agenda mostra antes de mandar para a tela cheia. */
const MAX_PROXIMOS = 5;

const isDataHoje = (data) => {
    const hoje = new Date();
    return (
        data.getDate() === hoje.getDate() &&
        data.getMonth() === hoje.getMonth() &&
        data.getFullYear() === hoje.getFullYear()
    );
};

/**
 * Situação de um compromisso futuro.
 *
 * A lista só tem data de hoje em diante, então "concluído" não aparece aqui — o que sobra é
 * distinguir o que já começou (hoje, no passado do relógio) do que está confirmado para depois.
 * `confirmada` guarda a data do aceite; nula significa que ninguém aceitou ainda.
 */
const situacaoDoCompromisso = (item) => {
    const data = item?.data ? new Date(item.data) : null;
    const dataValida = data instanceof Date && !Number.isNaN(data.getTime());

    if (item?.confirmada === null || item?.confirmada === undefined) {
        return "pendente";
    }

    if (dataValida && data < new Date() && isDataHoje(data)) {
        return "em_andamento";
    }

    return "confirmado";
};

const mapCompromisso = (item) => ({
    id: item?.id,
    // Quem atende. É o que separa, na mesma lista, o que a pessoa contratou do que ela atende.
    idUsuarioProfissional: item?.profissional?.idusuario ?? null,
    dataHora: item?.data,
    nomeProfissional: item?.profissional?.nome || "",
    nomeCliente: item?.nomeusuarioagenda || "",
    // O arquivo da foto, para o InitialsAvatar do compromisso em destaque. Os dois lados, pela mesma
    // razão do nome: a lista traz o que a pessoa contratou e o que ela atende.
    avatarProfissional: item?.profissional?.avatar || null,
    avatarCliente: item?.avatarusuarioagenda || null,
    // Faxina fixa: {id, intervaloSemanas, status, origem}, ou null para o pedido avulso. Mesma forma
    // da listagem da Minha agenda (AgendaController.recorrenciaParaTela).
    recorrencia: item?.recorrencia ?? null,
    endereco: item?.enderecousuarioagenda || "",
    situacao: situacaoDoCompromisso(item),
    avaliado: item?.avaliado ?? null
});

const safeRequest = async (requestFn, fallbackData, contexto) => {
    try {
        const response = await requestFn();
        return response.data;
    } catch (error) {
        console.error(`[dashboard] Falha ao carregar ${contexto}`, error);
        return fallbackData;
    }
};

/**
 * Os próximos compromissos, do mais cedo para o mais tarde.
 *
 * Vem de `/agenda/hoje`, que apesar do nome devolve uma janela de -7 a +14 dias (AgendaController:
 * segunda-feira, -7, depois +21). O painel anterior renderizava esse intervalo inteiro sob o título
 * "Agenda da semana", misturando o que já passou com as próximas duas semanas. O corte em `agora` e
 * a ordenação crescente moram aqui: a faixa da tela responde "o que vem agora", e o passado tem a
 * página de Agenda, que já tem filtros para isso.
 *
 * A consulta por trás do endpoint só traz confirmadas, então pedido ainda sem resposta NÃO chega
 * nesta lista — e é por isso que ele é contado à parte, em `pendencias.aguardandoResposta`, e
 * aparece no bloco "Precisa de você". Quem mexer aqui esperando ver pendentes vai procurar em vão.
 */
/**
 * O compromisso pertence ao modo da tela? `/agenda/hoje` traz os dois papéis juntos (a query é
 * `usuario.id OR p.idUsuario`). Com o seletor de modo, o painel "trabalhando" mostra só onde a
 * pessoa atende, e o "contratando" só o que ela contratou. Sem `userId` não há como saber, e a
 * lista passa inteira, como antes.
 */
const doPapel = (item, papel, userId) => {
    if (!userId || !papel) return true;
    const souQuemAtende = Number(item.idUsuarioProfissional) === Number(userId);
    return papel === "profissional" ? souQuemAtende : !souQuemAtende;
};

const dataDe = (item) => {
    const data = item.dataHora ? new Date(item.dataHora) : null;
    return data instanceof Date && !Number.isNaN(data.getTime()) ? data : null;
};

/**
 * Por quanto tempo a resposta do `/agenda/hoje` serve aos dois modos.
 *
 * A lista é a mesma nos dois (o papel é filtrado aqui, em `doPapel`), e é a chamada mais lenta do
 * painel: 3,7 s medidos contra 2,1 a 2,4 s de cada `/dashboard/*` (prompts/painel-troca-de-modo-instantanea.md).
 * Só a troca de modo e o pré-carregamento do outro modo aceitam a resposta recente. Abrir o painel
 * busca de novo, porque é voltando da Agenda, depois de aceitar um pedido, que a lista muda.
 */
const JANELA_AGENDA_MS = 60 * 1000;

/** A última `/agenda/hoje` pedida: { userId, quando, promessa }. Serve também de "em andamento". */
let agendaRecente = null;

/** A janela inteira do `/agenda/hoje`, nos dois papéis, em ordem crescente. Rejeita se a API falhar. */
const buscarAgenda = (userId, aceitaRecente) => {
    const recente = agendaRecente;
    if (
        aceitaRecente &&
        recente &&
        recente.userId === userId &&
        Date.now() - recente.quando < JANELA_AGENDA_MS
    ) {
        return recente.promessa;
    }

    const entrada = { userId, quando: Date.now() };
    entrada.promessa = apiClient.get('/agenda/hoje').then(({ data: dados }) => {
        const lista = Array.isArray(dados) ? dados : (dados ? [dados] : []);
        return lista
            .map(mapCompromisso)
            .filter((item) => dataDe(item) !== null)
            .sort((a, b) => dataDe(a) - dataDe(b));
    });
    // A falha não fica guardada: a próxima troca tenta de novo, em vez de herdar a lista vazia.
    entrada.promessa.catch(() => {
        if (agendaRecente === entrada) agendaRecente = null;
    });
    agendaRecente = entrada;
    return entrada.promessa;
};

const compromissosDoPapel = async (papel, userId, aceitaRecente = false) =>
    (await buscarAgenda(userId, aceitaRecente)).filter((item) => doPapel(item, papel, userId));

/** A janela inteira do `/agenda/hoje`, já no papel da tela e em ordem crescente. */
const buscarCompromissos = (papel, userId) =>
    compromissosDoPapel(papel, userId).catch((error) => {
        console.error("[dashboard] Falha ao carregar próximos compromissos", error);
        return [];
    });

const proximosDe = (compromissos) => {
    const agora = new Date();
    return compromissos
        // O compromisso de hoje que já começou continua sendo "o que vem agora" até o dia virar.
        .filter((item) => dataDe(item) >= agora || isDataHoje(dataDe(item)))
        .slice(0, MAX_PROXIMOS);
};

/**
 * Ontem, hoje e amanhã, sem teto: as abas do painel do profissional, que precisa saber quem atendeu
 * ontem (cobrar, conferir) e quem atende hoje e amanhã. A janela do `/agenda/hoje` começa na segunda
 * da semana passada e termina na segunda daqui a duas semanas, então os três dias sempre cabem nela.
 */
const tresDiasDe = (compromissos) => {
    const inicio = new Date();
    inicio.setHours(0, 0, 0, 0);
    inicio.setDate(inicio.getDate() - 1);
    const fim = new Date(inicio);
    fim.setDate(fim.getDate() + 3);

    return compromissos.filter((item) => dataDe(item) >= inicio && dataDe(item) < fim);
};

export const getProximosCompromissos = async (papel, userId) =>
    proximosDe(await buscarCompromissos(papel, userId));

/** As duas listas do painel a partir de uma requisição só. */
export const getCompromissosDoPainel = async (papel, userId) => {
    const compromissos = await buscarCompromissos(papel, userId);
    return { proximos: proximosDe(compromissos), tresDias: tresDiasDe(compromissos) };
};

const rotaDe = (perfil) => (perfil === "profissional" ? "profissional" : "usuario");

export const getIndicadores = async (perfil) =>
    safeRequest(
        () => apiClient.get(`/dashboard/${rotaDe(perfil)}`),
        vazioPorPerfil(perfil),
        `indicadores do dashboard (${rotaDe(perfil)})`
    );

/**
 * Tudo que o dashboard precisa, em paralelo, e se as duas chamadas deram certo.
 *
 * `Promise.all`, e não `await` em sequência como antes: as duas chamadas não dependem uma da outra,
 * e enfileirá-las somava as duas latências na primeira pintura — o que se paga caro no celular.
 * `allSettled` porque cada metade falha por conta própria: a tela mostra o que chegou, e só o
 * snapshot `completo` entra no cache (um zero de falha não pode reaparecer na próxima troca).
 */
const montarSnapshot = async (perfil, userId, aceitaAgendaRecente) => {
    const [compromissos, indicadores] = await Promise.allSettled([
        compromissosDoPapel(perfil, userId, aceitaAgendaRecente),
        apiClient.get(`/dashboard/${rotaDe(perfil)}`).then((response) => response.data)
    ]);

    if (compromissos.status === "rejected") {
        console.error("[dashboard] Falha ao carregar próximos compromissos", compromissos.reason);
    }
    if (indicadores.status === "rejected") {
        console.error(`[dashboard] Falha ao carregar indicadores do dashboard (${rotaDe(perfil)})`, indicadores.reason);
    }

    const lista = compromissos.status === "fulfilled" ? compromissos.value : [];

    return {
        snapshot: {
            proximos: proximosDe(lista),
            tresDias: tresDiasDe(lista),
            indicadores: (indicadores.status === "fulfilled" && indicadores.value) || vazioPorPerfil(perfil)
        },
        completo: compromissos.status === "fulfilled" && indicadores.status === "fulfilled"
    };
};

export const getDashboardSnapshot = async (perfil, userId) =>
    (await montarSnapshot(perfil, userId, false)).snapshot;

/**
 * O último snapshot completo de cada modo, por usuário: `${userId}:${perfil}`.
 *
 * É o que torna a troca "Trabalhando | Contratando" instantânea. Antes cada troca voltava ao
 * esqueleto e esperava 3,5 s pelas mesmas chamadas da troca anterior; agora a tela pinta com o que
 * já tem e revalida por trás (useDashboardData). A chave leva o usuário para que outra conta no
 * mesmo aparelho nunca veja os números da anterior, e o login esquece tudo (esquecerSnapshots).
 */
const snapshots = new Map();
const chaveDoSnapshot = (perfil, userId) => `${userId}:${perfil}`;

/** O snapshot guardado do modo, ou undefined. Síncrono, para a primeira pintura da troca. */
export const lerSnapshotCacheado = (perfil, userId) =>
    perfil && userId ? snapshots.get(chaveDoSnapshot(perfil, userId)) : undefined;

/**
 * Busca o snapshot do modo e o guarda se veio inteiro. `agendaRecente` aceita a `/agenda/hoje` dos
 * últimos 60 s (ver JANELA_AGENDA_MS): vale para a troca de modo, não para quem abre o painel.
 */
export const carregarSnapshot = async (perfil, userId, { agendaRecente = false } = {}) => {
    const { snapshot, completo } = await montarSnapshot(perfil, userId, agendaRecente);
    if (completo && userId) snapshots.set(chaveDoSnapshot(perfil, userId), snapshot);
    return snapshot;
};

/** Login novo, ou os testes: nenhum número da sessão anterior. */
export const esquecerSnapshots = () => {
    snapshots.clear();
    agendaRecente = null;
};

/**
 * Carrega o modo que não está na tela, para a troca já pintar com dados, e devolve quantas
 * pendências ele tem, para o ponto âmbar do seletor.
 *
 * A requisição já existia: o ponto âmbar sempre baixou os indicadores completos do outro modo, e
 * aproveitava só um número. Agora o resto vai para o cache. A `/agenda/hoje` sai da chamada que o
 * modo da tela acabou de fazer, então o custo de rede continua sendo um `/dashboard/{outro}`.
 *
 * Só conta o que é ação da própria pessoa: do lado profissional, os pedidos esperando a resposta
 * dela; do lado cliente, as faxinas dela esperando avaliação. "Pedido ainda sem profissional
 * confirmado" fica de fora, porque a vez é do outro.
 *
 * Falha devolve 0, sem erro na tela: é um aviso a mais, e o painel principal não depende dele.
 */
export const preCarregarOutroModo = async (perfilNaTela, userId) => {
    const outro = perfilNaTela === "profissional" ? "usuario" : "profissional";
    const { indicadores } = await carregarSnapshot(outro, userId, { agendaRecente: true });
    const pendencias = indicadores?.pendencias || {};
    return Number(
        (outro === "profissional" ? pendencias.aguardandoResposta : pendencias.avaliacoesPendentes) || 0
    );
};
